// ─────────────────────────────────────────────────────────────────────────────
// growthRunner.ts — WHAT BUCK IS ACTUALLY DOING.
//
// `GET /api/growth/runner` (server/app.py:6845, COMMITTED) answers the one
// question the toggle never could: ARMED IS NOT THE SAME AS WORKING. The route's
// own docstring says so, and it is the whole reason this client exists —
// `/api/rexy/agent-loop` stays generic across every agent and a Buck-shaped field
// on it would be the special case that rots.
//
// ── WHY THE CAPTION IS NO LONGER "ARMED" ───────────────────────────────────
// It used to be. On 2026-08-28 the investigation found the toggle was CONSENT
// AND NOT A LAUNCHER: nothing in the running server called `growth_harness.run`,
// so rendering an ON would have been an ON that started nothing. THAT IS NOW
// FALSE, and it was verified rather than taken on report:
//
//   server/app.py:6816  _start_growth_runner_supervisor()   — COMMITTED
//   growth_runner.py    SUPERVISOR_INTERVAL_SECONDS = 60    — COMMITTED
//   server/app.py:7070  started in the same else branch as the schedulers
//
// A level-triggered tick every 60 seconds reads the toggle and starts him. So
// `GROWTH:buck: true` now means Buck WORKS, including across a reboot, and the
// surface may say so.
//
// ── WHAT IS *NOT* COMMITTED, AND THE STRIP DEGRADES FOR IT ─────────────────
// PROVISIONAL — THE LINE THAT MOVES WHEN FORGE COMMITS. Measured, not assumed:
//
//   growth_runner.py `_live_from_round`   HEAD: 0 occurrences   worktree: 2
//   growth_runner.py `"live"` in status() HEAD: absent          worktree: line 641
//   app.py  the Buck kick (`_grun`)       HEAD: 0 occurrences   worktree: 1
//
// So a server booted from HEAD serves this route with NO `live` key at all. That
// is not "he is not walking" — it is "this build does not publish the detail",
// and the two must not render alike. `liveKnown` carries the difference.
//
// The missing kick costs only immediacy: without it the supervisor still starts
// him within 60 seconds, so the runner is whole. The surface says "within a
// minute" rather than implying an instant start it cannot promise.
// ─────────────────────────────────────────────────────────────────────────────

import { apiBase, authToken } from './apiBase';

export const GROWTH_RUNNER_PATH = '/growth/runner';

/**
 * The one-line answer the route composes. NOT DERIVED HERE, AND THAT IS THE
 * WHOLE RULE — see `readState` below.
 *
 * ── THE FOURTH WORD, AND WHY IT IS THE BACKEND'S TO SAY (owner ruling
 *    2026-09-01) ────────────────────────────────────────────────────────────
 * The owner pressed STOP, got nothing back, and went to SSH to find out what
 * was happening — the exact thing this control exists to end. The chip stayed
 * on STOP and the rail dot stayed GREEN while Buck was already disarmed and
 * finishing his round.
 *
 * NOTHING ON THIS SIDE WAS LYING. `growth_runner.status()` composes:
 *
 *     "state": ("walking" if running else
 *               "armed, not walking" if on else
 *               "off"),
 *
 * `running` is tested FIRST and UNCONDITIONALLY, so `armed=False` +
 * `running=True` — disarmed, round still in flight — COMPOSES TO "walking".
 * The state had no word, every surface was told "walking", and every surface
 * rendered it honestly.
 *
 * THE FIX WAS NOT TO COMPOSE IT HERE. Deriving `!armed && running` client-side
 * would build the second copy of the route's own rule that `readState` refuses
 * by name, and this build has spent a week removing exactly that. So FORGE ADDS
 * THE WORD AND THIS FILE RENDERS IT: one author for the state, still the route.
 *
 * ── THE STRING THIS CLIENT READS ──────────────────────────────────────────
 * `"stopping"`. One word, no comma — `"armed, not walking"` has to be matched
 * as a sentence and this one does not. Expected shape of the emitter, which is
 * a reorder rather than a new branch:
 *
 *     "state": ("stopping" if running and not on else
 *               "walking"  if running else
 *               "armed, not walking" if on else
 *               "off"),
 *
 * UNTIL THAT LANDS NOTHING EMITS IT, and this client is correct either way: an
 * older build keeps saying "walking" through a stop, which is the defect, not a
 * crash. The render lights up the moment his word arrives — the same way the
 * lane filters waited on his lane fields rather than inventing them.
 */
export type RunnerState =
  /** A walk is in flight and the owner has not withdrawn consent. */
  | 'walking'
  /**
   * DISARMED, AND THE ROUND IS STILL IN FLIGHT. He is stopping and he is not
   * stopped — up to several minutes, because a repair round does full network
   * verifications per company and HE IS NEVER KILLED MID-COMPANY.
   */
  | 'stopping'
  /** The toggle is on and no walk is running — the tick has not landed, or he
   *  stopped himself. `coolingUntil` is what tells those two apart. */
  | 'armed_not_walking'
  /**
   * HALTED ON AN OUTAGE (owner ruling 2026-09-14, STOP AND ALERT). Armed, not
   * walking, and NOT cooling: a round moved nothing and every company in it
   * named the contact lane. The runner will not restart him until he is
   * re-armed. `halt.alert` names what is down.
   */
  | 'halted'
  /** The toggle is off. */
  | 'off'
  /** We could not read the route. NOT a state the backend reports. */
  | 'unknown';

/**
 * BUCK'S CYCLE (owner ruling 2026-09-28): "the screens that report 'finished of
 * fifty' show the cycle instead — state, trade, new this cycle." Named by the
 * producer (`growth_runner.cycle_view`) and painted here, never derived: every
 * number is the backend's own, and a figure it did not send stays null.
 */
export interface RunnerCycle {
  state: string | null;
  trade: string | null;
  tradeNumber: number | null;
  trades: number | null;
  stateNumber: number | null;
  states: number | null;
  newThisTrade: number | null;
  perTrade: number | null;
  newThisState: number | null;
  stateQuota: number | null;
  pass: number | null;
  pending: number | null;
}

/** The five facts the strip shows, off one round entry. */
export interface RunnerLive {
  lane: { state?: string; metro_region?: string; suburb_cluster?: string; sector?: string };
  /** The backend's own joined label. Preferred over re-joining the parts here. */
  laneLabel: string | null;
  round: number | null;
  roundsTarget: number | null;
  companiesComplete: number | null;
  companiesInRound: number | null;
  /** Why the harness chose THIS work, in the doctrine's own words. */
  lastReason: string | null;
  /** The cycle at this company, or null from a build that does not publish it. */
  cycle: RunnerCycle | null;
}

/**
 * THE LAST FINISHED WALK. `growth_runner`'s own `outcome` object.
 *
 * THIS IS WHAT MAKES "IDLE AFTER CLEARING EVERYTHING" DIFFERENT FROM "IDLE AT
 * BOOT" (owner ruling 2026-08-31). `_last` is module state: null in a freshly
 * booted process, populated the moment a walk ends. Nothing else on this route
 * can tell those two apart, and they are not the same thing to look at.
 */
export interface RunnerLast {
  /** How many rounds that walk banked. */
  rounds: number | null;
  /** Why it ended, in the runner's own words. */
  stopped: string | null;
  /** Did the OWNER end it, or did it finish on its own? Different facts. */
  stoppedByOwner: boolean;
  endedAt: string | null;
  seconds: number | null;
}

/** WHAT IS DOWN, in the runner's own words. `status()["halt"]`. */
export interface RunnerHalt {
  /** "contact lane" — the thing that is down. */
  down: string | null;
  /** The full alert: "The contact lane did not answer for 10 of 10 companies …". */
  alert: string | null;
  /** The seam's ground, e.g. `runner_failed`. */
  ground: string | null;
  companies: number | null;
  haltedAt: string | null;
}

/** One company in the walk's round, as `status()` publishes it. */
export interface RunnerCompany {
  companyId: string;
  /** The name Places discovered. Null when the profile carries none — never the id restated. */
  name: string | null;
}

export interface RunnerStatus {
  /** Did we read the route at all? */
  reachable: boolean;
  state: RunnerState;
  /** The stored consent. Fails closed to false. */
  armed: boolean;
  /** Whether anyone has ruled on it — separate from `armed`, as everywhere else. */
  ruled: boolean;
  /**
   * TRUE only when we know whether there is round detail. When `state` is
   * 'walking' and this is false, the build does not publish `live` — which is a
   * different fact from "there is no round in flight" and says so.
   */
  liveKnown: boolean;
  live: RunnerLive | null;
  /** The last finished walk, or null when this process has run none. */
  last: RunnerLast | null;
  /** The stored consent's own reason — about the SETTING, not about a walk. */
  consentWhy: string | null;
  /**
   * WHEN THE RUNNER WILL CONSIDER STARTING HIM AGAIN, or null when it will
   * consider it now. ISO-8601, straight off `status()["cooling_until"]`.
   *
   * THIS FIELD WAS ON THE WIRE AND THIS CLIENT DID NOT READ IT, AND TWO
   * SENTENCES ON SCREEN WERE FALSE BECAUSE OF IT (owner ruling 2026-09-01):
   * the strip said "ARMED · NOT WALKING YET — A CHECK EVERY 60s STARTS HIM" and
   * the dot's title said "ON — NOTHING DUE". During a cooldown the supervisor
   * DOES fire every 60s and `start()` ACTIVELY REFUSES for up to fifteen
   * minutes (`COOLDOWN_SECONDS = 900`, growth_runner.py:143, declined at :583),
   * and "nothing due" was claimed with 22 companies due.
   *
   * IT IS ALSO THE ONLY THING THAT SEPARATES THE TWO WAYS OF BEING ARMED AND
   * IDLE. The runner sets it in `_walk`'s `finally` ONLY when the owner did NOT
   * stop him (growth_runner.py:527-531), so its presence means HE STOPPED
   * HIMSELF — the harness's anti-spin rule, "a full round moved nothing —
   * repeating them is a spin, not work" (growth_harness.py:1180). Green would
   * be wrong: he is not walking. Red would be wrong: the owner did not stop him.
   */
  coolingUntil: string | null;
  /** Present only while `state` is `halted`; null otherwise. */
  halt: RunnerHalt | null;
  /**
   * WHERE THE WALK IS INSIDE ITS ROUND (owner ruling 2026-09-15). Both rows read
   * NOT PUBLISHED through a whole live walk, so a working run and a hung one
   * looked the same. TRUE only when the build publishes BOTH keys — an older
   * build that sends neither has told us nothing, exactly as with `liveKnown`.
   */
  positionKnown: boolean;
  /** The company whose stages are running now. Null while walking = between rounds. */
  currentCompany: RunnerCompany | null;
  /** The rest of this round, in the order it will be walked. */
  queue: RunnerCompany[];
  /**
   * TRUE when the build publishes `cycle` at all (the key is present, even as
   * null = "no position yet"). An older build sends no key and has told us
   * nothing — the same honesty split as `liveKnown`.
   */
  cycleKnown: boolean;
  /** Where the cycle is, walking or not — it survives Off and a restart. */
  cycle: RunnerCycle | null;
}

export const UNREAD_RUNNER: RunnerStatus = {
  reachable: false, state: 'unknown', armed: false, ruled: false,
  liveKnown: false, live: null, last: null, consentWhy: null, coolingUntil: null,
  halt: null, positionKnown: false, currentCompany: null, queue: [],
  cycleKnown: false, cycle: null,
};

// apiBase and authToken are SHARED (src/api/apiBase.ts). This file used to
// carry its own copy of both, as five sibling clients did — six identical
// hardcodings of a host, and a built bundle pointed every one of them at
// herexy.com wherever it was served from. An instance fix leaves twins, so the
// answer moved to one module rather than being corrected here.


/** Own-property, trimmed, non-empty, or undefined. Never walks the prototype chain. */
function ownString(d: any, key: string): string | undefined {
  if (d === null || typeof d !== 'object') return undefined;
  if (!Object.prototype.hasOwnProperty.call(d, key)) return undefined;
  const v = (d as Record<string, unknown>)[key];
  if (typeof v !== 'string') return undefined;
  const t = v.trim();
  return t.length > 0 ? t : undefined;
}

/** A finite non-negative integer the body OWNS, or null. `0` is a real answer
 *  and must survive — "0 complete" is the honest first minute of a round. */
function ownCount(d: any, key: string): number | null {
  if (d === null || typeof d !== 'object') return null;
  if (!Object.prototype.hasOwnProperty.call(d, key)) return null;
  const v = (d as Record<string, unknown>)[key];
  if (typeof v !== 'number' || !Number.isFinite(v) || v < 0) return null;
  return Math.floor(v);
}

/** A boolean the body OWNS. Never coerced — same discipline as the toggle. */
function ownBool(d: any, key: string): boolean {
  if (d === null || typeof d !== 'object') return false;
  if (!Object.prototype.hasOwnProperty.call(d, key)) return false;
  return (d as Record<string, unknown>)[key] === true;
}

/** A finite number the body OWNS, or null. Unlike `ownCount` this keeps a
 *  fractional value — `seconds` is reported to one decimal place. */
function ownNumber(d: any, key: string): number | null {
  if (d === null || typeof d !== 'object') return null;
  if (!Object.prototype.hasOwnProperty.call(d, key)) return null;
  const v = (d as Record<string, unknown>)[key];
  return (typeof v === 'number' && Number.isFinite(v) && v >= 0) ? v : null;
}

function readLast(last: any): RunnerLast | null {
  if (last === null || typeof last !== 'object' || Array.isArray(last)) return null;
  return {
    rounds: ownCount(last, 'rounds'),
    stopped: ownString(last, 'stopped') ?? null,
    stoppedByOwner: ownBool(last, 'stopped_by_owner'),
    endedAt: ownString(last, 'ended_at') ?? null,
    seconds: ownNumber(last, 'seconds'),
  };
}

/**
 * The tokens whose WIRE spelling is identical to the token. Belt-and-braces
 * with the explicit lines in `readState` — deliberately, and `walking` and
 * `off` have been in both since this file shipped.
 *
 * SO NEITHER SITE ALONE IS OBSERVABLE, and the mutation proof said so: deleting
 * `stopping` from `readState` changed no behaviour, because this array still
 * mapped it. It is recorded here rather than tidied away, so the next reader
 * does not delete one of the two and believe they have changed something. THE
 * MAPPING IS GONE ONLY WHEN BOTH ARE — which is the fault F1 now injects.
 */
const STATES: readonly RunnerState[] = ['walking', 'stopping', 'halted', 'armed_not_walking', 'off'];

/** The backend spells it with a comma and spaces; the wire value is mapped once,
 *  here, rather than compared as a sentence at four call sites. */
function readState(runner: any): RunnerState {
  const raw = ownString(runner, 'state');
  if (raw === 'walking') return 'walking';
  if (raw === 'stopping') return 'stopping';
  if (raw === 'halted') return 'halted';
  if (raw === 'off') return 'off';
  if (raw === 'armed, not walking') return 'armed_not_walking';
  // Anything else — including a state this client has not been taught — is
  // unknown. It is never guessed from `armed`/`running`, which would be a second
  // copy of the rule the route already composes.
  return STATES.indexOf(raw as RunnerState) >= 0 ? (raw as RunnerState) : 'unknown';
}

function readHalt(halt: any): RunnerHalt | null {
  if (halt === null || typeof halt !== 'object' || Array.isArray(halt)) return null;
  return {
    down: ownString(halt, 'down') ?? null,
    alert: ownString(halt, 'alert') ?? null,
    ground: ownString(halt, 'ground') ?? null,
    companies: ownCount(halt, 'companies'),
    haltedAt: ownString(halt, 'halted_at') ?? null,
  };
}

function readCompany(c: any): RunnerCompany | null {
  if (c === null || typeof c !== 'object' || Array.isArray(c)) return null;
  const companyId = ownString(c, 'company_id');
  if (!companyId) return null;
  return { companyId, name: ownString(c, 'name') ?? null };
}

function readQueue(q: any): RunnerCompany[] {
  if (!Array.isArray(q)) return [];
  return q.map(readCompany).filter((c): c is RunnerCompany => c !== null);
}

function readCycle(c: any): RunnerCycle | null {
  if (c === null || typeof c !== 'object' || Array.isArray(c)) return null;
  return {
    state: ownString(c, 'state') ?? null,
    trade: ownString(c, 'trade') ?? null,
    tradeNumber: ownCount(c, 'trade_number'),
    trades: ownCount(c, 'trades'),
    stateNumber: ownCount(c, 'state_number'),
    states: ownCount(c, 'states'),
    newThisTrade: ownCount(c, 'new_this_trade'),
    perTrade: ownCount(c, 'per_trade'),
    newThisState: ownCount(c, 'new_this_state'),
    stateQuota: ownCount(c, 'state_quota'),
    pass: ownCount(c, 'pass'),
    pending: ownCount(c, 'pending'),
  };
}

function readLive(live: any): RunnerLive | null {
  if (live === null || typeof live !== 'object' || Array.isArray(live)) return null;
  const lane = (live.lane && typeof live.lane === 'object' && !Array.isArray(live.lane))
    ? live.lane : {};
  return {
    lane: {
      state: ownString(lane, 'state'),
      metro_region: ownString(lane, 'metro_region'),
      suburb_cluster: ownString(lane, 'suburb_cluster'),
      sector: ownString(lane, 'sector'),
    },
    laneLabel: ownString(live, 'lane_label') ?? null,
    round: ownCount(live, 'round'),
    roundsTarget: ownCount(live, 'rounds_target'),
    companiesComplete: ownCount(live, 'companies_complete'),
    companiesInRound: ownCount(live, 'companies_in_round'),
    lastReason: ownString(live, 'last_reason') ?? null,
    cycle: readCycle(live.cycle),
  };
}

/** The wire body → what the strip renders. NEVER throws. */
export function normalizeRunner(d: any): RunnerStatus {
  if (!d || typeof d !== 'object') return UNREAD_RUNNER;
  const runner = (d.runner && typeof d.runner === 'object') ? d.runner : null;
  if (!runner) return UNREAD_RUNNER;

  const state = readState(runner);
  const live = readLive(runner.live);

  return {
    reachable: true,
    state,
    armed: Object.prototype.hasOwnProperty.call(runner, 'armed') && runner.armed === true,
    ruled: Object.prototype.hasOwnProperty.call(runner, 'ruled') && runner.ruled === true,
    // THE HONESTY SPLIT. The key being PRESENT is what makes the detail known —
    // a build that publishes `live: null` while not walking has told us
    // something; a build with no `live` key at all has told us nothing.
    liveKnown: Object.prototype.hasOwnProperty.call(runner, 'live'),
    live,
    // `ownString(runner.last, 'why')` STOOD HERE AND COULD NEVER MATCH. The
    // runner's `outcome` object has no `why` key at all — it carries `stopped`
    // — so the whole expression always fell through to `consent_why`, and the
    // last walk's outcome was unreachable from this client. Found while wiring
    // the round feedback (owner ruling 2026-08-31); the two facts are now read
    // separately because they are about different things: one is why a WALK
    // ended, the other is why the SETTING is what it is.
    last: readLast(runner.last),
    consentWhy: ownString(runner, 'consent_why') ?? null,
    // `status()` writes this key ONLY while `_now() < _cooldown_until` and null
    // otherwise, so an absent key and a null value mean the same thing here and
    // no `coolingKnown` split is owed. That is NOT true of `live`, whose key can
    // be missing from an older build entirely — hence `liveKnown` above.
    coolingUntil: ownString(runner, 'cooling_until') ?? null,
    halt: readHalt(runner.halt),
    // BOTH KEYS OR NOTHING: `status()` always writes the pair, so one without
    // the other is not this producer and is read as unpublished.
    positionKnown: Object.prototype.hasOwnProperty.call(runner, 'current_company')
      && Object.prototype.hasOwnProperty.call(runner, 'queue'),
    currentCompany: readCompany(runner.current_company),
    queue: readQueue(runner.queue),
    cycleKnown: Object.prototype.hasOwnProperty.call(runner, 'cycle'),
    cycle: readCycle(runner.cycle),
  };
}

/**
 * DELTA'S ANSWER TO THE SAME QUESTION, IN THE SAME WORDS (owner ruling
 * 2026-09-23). `server/delta_activity.status()` composes Buck's vocabulary from
 * Delta's supervisors and work claims, so this client — `normalizeRunner`,
 * `readState` and every dot built on them — reads it unchanged. One reader, two
 * producers; never a second reader for the second agent.
 */
export const DELTA_ACTIVITY_PATH = '/delta/activity';

/** GET Buck's runner state. Never rejects — an unread route is a state the strip
 *  must render, not an exception the caller has to catch. */
export async function getRunnerStatus(): Promise<RunnerStatus> {
  return getRunnerStatusAt(GROWTH_RUNNER_PATH);
}

/** The same read against any route that publishes Buck's shape. */
export async function getRunnerStatusAt(path: string): Promise<RunnerStatus> {
  let res: Response;
  try {
    res = await fetch(`${apiBase()}${path}`, {
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken()}` },
    });
  } catch {
    return UNREAD_RUNNER;
  }
  if (!res.ok) return UNREAD_RUNNER;
  try {
    return normalizeRunner(await res.json());
  } catch {
    return UNREAD_RUNNER;
  }
}
